import React, { useState, useEffect, useMemo } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner
} from "reactstrap";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useGetMailProfile, useUpdateMailProfile } from "api/mail-profiles/service";
import { useUpdateTenant } from "api/tenant/service";
import { getActiveColorStyle } from "utils/colorUtils";
import Select from "components/Select";
import { useTheme2FormPage, Theme2FormFieldsStyle, THEME2_ADMIN_INSETS } from "hooks/useTheme2FormPage";

const EmailSettings = ({ activeColor = "blue", account, shellThemeVariant = "1" }) => {
    const { t } = useTranslation();
    const mailId = account?.mail_id;
    const { data: mailData, isLoading } = useGetMailProfile(mailId);

    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const {
        inputStyle,
        inputClassName,
        selectStyles,
        theme2FieldsCss,
        contentClassName,
        formFieldsClassName,
    } = useTheme2FormPage({
        shellThemeVariant,
        activeColorStyle,
        pageKey: "email-settings",
        insetClass: THEME2_ADMIN_INSETS.emailSettings,
        withSelectStyles: true,
    });
    const updateMailMutation = useUpdateMailProfile();
    const updateTenantMutation = useUpdateTenant();

    const encryptionOptions = [
        { value: "none", label: "None" },
        { value: "ssl_tls", label: "SSL/TLS" },
        { value: "starttls", label: "STARTTLS" },
    ];

    const [formData, setFormData] = useState({
        host: "",
        port: "",
        username: "",
        password: "",
        encryption: "none",
        from_name: "",
        from_email: ""
    });
    const [recordId, setRecordId] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        // If account has email_settings (Tenant context), use those
        if (account?.mail_profile) {
            setFormData({
                host: account.mail_profile.host || "",
                port: account.mail_profile.port || "",
                username: account.mail_profile.username || "",
                password: account.mail_profile.password || "",
                encryption: account.mail_profile.encryption || "none",
                from_name: account.mail_profile.from_name || "",
                from_email: account.mail_profile.from_email || ""
            });
            return;
        }

        // Otherwise use mail profile data
        let profile = null;
        if (mailData?.data) {
            profile = Array.isArray(mailData.data) ? mailData.data[0] : mailData.data;
        } else if (mailData?.results) {
            profile = Array.isArray(mailData.results) ? mailData.results[0] : mailData.results;
        }

        if (profile) {
            setRecordId(profile.id);
            const attributes = profile.attributes || profile;

            setFormData({
                host: attributes.host || "",
                port: attributes.port || "",
                username: attributes.username || "",
                password: attributes.password || "",
                encryption: attributes.encryption || "none",
                from_name: attributes.from_name || "",
                from_email: attributes.from_email || ""
            });
        }
    }, [mailData, account]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === "port" ? (value === "" ? "" : parseInt(value)) : value
        }));
    };

    const handleSave = (e) => {
        e.preventDefault();

        if (account?.id) {
            // Update via tenant service if account is provided (Tenant context)
            updateTenantMutation.mutate({
                id: account.id,
                data: {
                    mail_profile: formData // Sending as requested: nested inside email details
                }
            });
        } else if (mailId) {
            // Original behavior (Settings context)
            updateMailMutation.mutate({
                id: mailId,
                data: formData
            });
        }
    };

    if (isLoading) {
        return (
            <div className={contentClassName}>
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "200px" }}
                >
                    <div
                        className="spinner-border"
                        role="status"
                        style={{ color: activeColorStyle.borderColor }}
                    >
                        <span className="sr-only">{t("emailSettings.loading")}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={contentClassName}>
            <Theme2FormFieldsStyle css={theme2FieldsCss} />
            <style>
                {`
          .email-settings-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .email-settings-card-header h2,
          .email-settings-card-header h4 {
            font-size: 16px !important;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .email-settings-page .email-settings-card-header > .d-flex {
            margin-top: 3px;
          }
          .email-settings-page .card .card-body {
            padding-top: 0px !important;
            padding-bottom: 0 !important;
            padding-left: 15px;
            padding-right: 15px;
          }
        `}
            </style>
            <Row>
                <Col md="12">
                    <Card>
                        <CardHeader className="email-settings-card-header">
                            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
                                <h2 className="mb-0">{t("routes.emailSettings")}</h2>

                                {/* Desktop View Buttons */}
                                <div className="d-none d-lg-flex align-items-center">
                                    <Button
                                        color="primary"
                                        size="sm"
                                        type="submit"
                                        form="email-settings-form"
                                        style={{ ...activeColorStyle, color: 'white' }}
                                        disabled={updateMailMutation.isPending || updateTenantMutation.isPending}
                                    >
                                        {updateMailMutation.isPending || updateTenantMutation.isPending ? (
                                            <>
                                                <Spinner size="sm" className="mr-1" />
                                                {t("emailSettings.updating")}
                                            </>
                                        ) : (
                                            <>
                                                <i className="tim-icons icon-check-2 mr-1" />
                                                {t("emailSettings.update")}
                                            </>
                                        )}
                                    </Button>
                                </div>

                                {/* Mobile View Buttons */}
                                <div className="d-flex d-lg-none flex-column w-100 mt-3">
                                    <Button
                                        color="primary"
                                        type="submit"
                                        form="email-settings-form"
                                        style={{ ...activeColorStyle, color: 'white', padding: '12px' }}
                                        disabled={updateMailMutation.isPending || updateTenantMutation.isPending}
                                        className="w-100"
                                    >
                                        {updateMailMutation.isPending || updateTenantMutation.isPending ? t("emailSettings.updating") : t("emailSettings.update")}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <form
                                id="email-settings-form"
                                onSubmit={handleSave}
                                className={formFieldsClassName}
                            >
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>{t("emailSettings.host")}</Label>
                                            <Input
                                                name="host"
                                                placeholder="smtp.example.com"
                                                type="text"
                                                className={inputClassName()}
                                                style={inputStyle}
                                                value={formData.host}
                                                onChange={handleChange}
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>{t("emailSettings.port")}</Label>
                                            <Input
                                                name="port"
                                                placeholder="587"
                                                type="number"
                                                className={inputClassName()}
                                                style={inputStyle}
                                                value={formData.port}
                                                onChange={handleChange}
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>{t("emailSettings.username")}</Label>
                                            <Input
                                                name="username"
                                                placeholder="username@example.com"
                                                type="text"
                                                className={inputClassName()}
                                                style={inputStyle}
                                                value={formData.username}
                                                onChange={handleChange}
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>{t("emailSettings.password")}</Label>
                                            <div style={{ position: 'relative' }}>
                                                <Input
                                                    name="password"
                                                    placeholder="********"
                                                    type={showPassword ? "text" : "password"}
                                                    className={inputClassName()}
                                                    style={inputStyle}
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <span
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    style={{
                                                        position: "absolute",
                                                        right: "10px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        cursor: "pointer",
                                                        color: "#aaa",
                                                        zIndex: 10
                                                    }}
                                                >
                                                    {showPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                                                </span>
                                            </div>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>{t("emailSettings.encryption")}</Label>
                                            <Select
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                name="encryption"
                                                value={encryptionOptions.find(opt => opt.value === formData.encryption)}
                                                onChange={(option) => setFormData(prev => ({ ...prev, encryption: option.value }))}
                                                options={encryptionOptions}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <hr />
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>{t("emailSettings.fromName")}</Label>
                                            <Input
                                                name="from_name"
                                                placeholder="Dealer Name"
                                                type="text"
                                                className={inputClassName()}
                                                style={inputStyle}
                                                value={formData.from_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>{t("emailSettings.fromEmail")}</Label>
                                            <Input
                                                name="from_email"
                                                placeholder="no-reply@example.com"
                                                type="email"
                                                className={inputClassName()}
                                                style={inputStyle}
                                                value={formData.from_email}
                                                onChange={handleChange}
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                {/* Bottom buttons removed for mobile-responsive consistency */}
                                <div className="d-none d-lg-block mt-4">
                                </div>
                            </form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default EmailSettings;
